/**
 * Push Notification Initializer Component
 * Uses Firebase Cloud Messaging for iOS push notifications
 */
import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Capacitor } from '@capacitor/core';
import { FirebaseMessaging } from '@capacitor-firebase/messaging';
import { toast } from 'sonner';
import { API } from '../lib/api';

export default function PushNotificationInitializer() {
  const { token, isAuthenticated } = useAuth();
  const [deviceToken, setDeviceToken] = useState(null);

  // Request permission and get token on app start
  useEffect(() => {
    const initFirebaseMessaging = async () => {
      if (!Capacitor.isNativePlatform()) {
        console.log('Firebase Messaging: Not a native platform');
        return;
      }

      try {
        console.log('Firebase Messaging: Requesting permissions...');
        
        // Request permission
        const permResult = await FirebaseMessaging.requestPermissions();
        console.log('Firebase Messaging: Permission result:', permResult.receive);
        
        if (permResult.receive === 'granted') {
          // Get FCM token
          const tokenResult = await FirebaseMessaging.getToken();
          console.log('Firebase Messaging: FCM Token:', tokenResult.token);
          
          setDeviceToken(tokenResult.token);
          localStorage.setItem('fcmToken', tokenResult.token);
          
          // Listen for token refresh
          await FirebaseMessaging.addListener('tokenReceived', (event) => {
            console.log('Firebase Messaging: Token refreshed:', event.token);
            setDeviceToken(event.token);
            localStorage.setItem('fcmToken', event.token);
          });

          // Listen for foreground notifications
          await FirebaseMessaging.addListener('notificationReceived', (event) => {
            console.log('Firebase Messaging: Notification received:', event.notification);
            toast.info(event.notification.title, {
              description: event.notification.body,
              duration: 5000
            });
          });

          // Listen for notification tap
          await FirebaseMessaging.addListener('notificationActionPerformed', (event) => {
            console.log('Firebase Messaging: Notification tapped:', event);
            handleNotificationTap(event.notification.data);
          });
        } else {
          console.log('Firebase Messaging: Permission denied');
        }
      } catch (error) {
        console.error('Firebase Messaging: Error:', error);
      }
    };

    initFirebaseMessaging();
  }, []);

  // Register device token with backend when user logs in
  useEffect(() => {
    const registerWithBackend = async () => {
      const storedToken = deviceToken || localStorage.getItem('fcmToken');
      
      if (!isAuthenticated || !token || !storedToken) {
        return;
      }

      try {
        console.log('Firebase Messaging: Registering with backend...');
        const response = await fetch(`${API}/devices/register`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            token: storedToken,
            platform: 'ios',
            device_name: 'iOS Device (FCM)'
          })
        });

        if (response.ok) {
          console.log('Firebase Messaging: Device registered with backend');
        } else {
          console.error('Firebase Messaging: Failed to register with backend');
        }
      } catch (error) {
        console.error('Firebase Messaging: Error registering:', error);
      }
    };

    registerWithBackend();
  }, [isAuthenticated, token, deviceToken]);

  const handleNotificationTap = (data) => {
    if (!data) return;
    
    switch (data.type) {
      case 'new_booking':
        window.location.href = '/admin/bookings';
        break;
      case 'booking_confirmed':
      case 'booking_cancelled':
        window.location.href = '/dashboard';
        break;
      case 'new_event':
        window.location.href = '/events';
        break;
      default:
        break;
    }
  };

  return null;
}
